import {FC, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import useAppStore from "../../store";
import {AnimatePresence, motion} from "framer-motion";
import {siteCookies, getValues} from "../../utils/cookies";
import Group from "./Group";
import AcceptedCookies from "../../types/AcceptedCookies";
import useAmplitude from "../../hooks/useAmplitude";

const Cookie:FC = () => {
    const {t} = useTranslation();
    const {showCookies, setShowCookies, setAllowScroll, setUserCookies} = useAppStore();
    const [isOpen, setIsOpen] = useState(false);
    const [acceptedCookies, setAcceptedCookies] = useState<AcceptedCookies>(getValues(true));
    const [changed, setChanged] = useState(false);
    const {send} = useAmplitude();

    useEffect(() => {
        setChanged(Object.values(acceptedCookies).some(value => !value));
    }, [acceptedCookies]);

    const setCookiesToStorage = (cookies: AcceptedCookies) => {
        localStorage.setItem('cookie_select', JSON.stringify(cookies));
        setUserCookies(cookies);
        send('Cookie_Banner', cookies);
        setAllowScroll(true);
        setShowCookies(false);
    }

    const handleClick = () => {
        setCookiesToStorage(getValues(true));
    }

    const handleSecondary = () => {
        if(!isOpen) {
            setAllowScroll(false);
            setIsOpen(true);
        } else {
            if(!changed) {
                setCookiesToStorage(getValues(false));
            } else {
                setCookiesToStorage(acceptedCookies);
            }
        }
    }

    const toggleCookies = (name: string) => {
        setAcceptedCookies({...acceptedCookies, [name]: !acceptedCookies[name]})
    }

    return <AnimatePresence>
        {showCookies && (
            <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ duration: 0.3, type: "spring", stiffness: 200, damping: 25 }}
                className={"fixed z-50 bottom-0 left-0 w-full"}
            >
                <div className="w-full max-w-md mx-auto">
                    <div className="bg-white text-primary p-3 rounded-t-xl">
                        <h3 className="font-bold text-sm">{t('cookies.title')}</h3>
                        <p className="text-sm">{t('cookies.description')}</p>

                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                        ><div className="border-t border-disabled-300 pt-2 mt-2" /></motion.div>

                        <motion.div
                            initial={{ height: 0, opacity: 0, overflowY: "hidden" }}
                            animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0, overflowY: isOpen ? "auto" : "hidden" }}
                            transition={{ duration: 0.3 }}
                            className="max-h-[65vh]"
                        >
                            <div className="h-full flex flex-col gap-2">
                                {
                                    siteCookies.map((group, index) => {
                                        return <Group
                                            key={index}
                                            title={group.title}
                                            description={group.description}
                                            disabled={group.disabled}
                                            name={group.name}
                                            services={group.services}
                                            toggle={toggleCookies}
                                        />
                                    })
                                }
                            </div>
                        </motion.div>

                        <div className="grid grid-cols-2 gap-3 mt-4 items-center">
                            <button onClick={handleClick} className="button button__xs">{t('cookies.accept')}</button>
                            <button onClick={handleSecondary} className="button__default button__xs">{isOpen ? (changed ? t('cookies.save') : t('cookies.decline')) : t('cookies.manage')}</button>
                        </div>
                    </div>
                </div>
            </motion.div>
        )}
    </AnimatePresence>
}

export default Cookie;